import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Image,
  Input,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import {
  addAdBannerApi,
  deleteAdBannerApi,
  editAdBannerApi,
  getAdBannerDetailApi,
  getAdBannerListApi,
  getAdPositionsApi,
  moveDownAdBannerApi,
  moveUpAdBannerApi,
} from '@/api/member/member';
import ImageUploader from '@/component/ImageUploader';
import { INTEGRAL_HEAD_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';

const { RangePicker } = DatePicker;

const IntegralMallHead: React.FC = () => {
  const { currentShopId } = useSnapshot(state);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [positions, setPositions] = useState<any[]>([]);
  const [createVisible, setCreateVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [editing, setEditing] = useState<{ id?: number | string; adId?: number | string; sortWeight?: number } | null>(
    null,
  );
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | string | null>(null);

  const fetchPositions = async () => {
    try {
      const res = await getAdPositionsApi();
      if (res?.code === 0 || res?.code === 200) {
        setPositions((res.data || []).map((p: any) => ({ label: p.name, value: p.code })));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchList = async (override?: { pageNum?: number; pageSize?: number }) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const values = form.getFieldsValue();
    const params: any = {
      adId: values.adId,
      title: values.title,
      position: values.position,
      status: values.status,
      createTimeStart: values.createTime?.length ? dayjs(values.createTime[0]).format('YYYY-MM-DD HH:mm:ss') : '',
      createTimeEnd: values.createTime?.length ? dayjs(values.createTime[1]).format('YYYY-MM-DD HH:mm:ss') : '',
      pageNo: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
      shopId: currentShopId,
    };
    setLoading(true);
    try {
      const res = await getAdBannerListApi(params);
      if (res?.code === 200 || res?.code === 0) {
        setData(res.rows || res.data?.rows || []);
        setTotal(res.total || res.data?.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      } else {
        message.error(res?.msg || '获取列表失败');
      }
    } catch (error) {
      message.error('获取列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  useEffect(() => {
    fetchList({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  const handleEdit = async (record: any) => {
    try {
      const res = await getAdBannerDetailApi(record.id || record.adId);
      if (res?.code === 200 || res?.code === 0) {
        const detail = res.data || res;
        setEditing({ id: detail.id, adId: detail.adId, sortWeight: detail.sortWeight });
        createForm.setFieldsValue({
          title: detail.title,
          position: detail.position,
          linkUrl: detail.linkUrl,
          status: detail.status === 1,
          image: detail.imageUrl || detail.tmImageUrl || detail.jdImageUrl,
        });
        setCreateVisible(true);
      } else {
        message.error(res?.msg || '获取详情失败');
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleDelete = (record: any) => {
    Modal.confirm({
      title: '确认删除该广告？',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await deleteAdBannerApi(record.id || record.adId);
          if (res?.code === 200 || res?.code === 0) {
            message.success('删除成功');
            fetchList({ pageNum: 1 });
          } else {
            message.error(res?.msg || '删除失败');
          }
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const handleMove = async (record: any, type: 'UP' | 'DOWN') => {
    try {
      const api = type === 'UP' ? moveUpAdBannerApi : moveDownAdBannerApi;
      const filterValues = form.getFieldsValue();
      const params: any = {
        adId: filterValues.adId,
        title: filterValues.title,
        position: filterValues.position,
        status: filterValues.status,
        createTimeStart: filterValues.createTime?.length
          ? dayjs(filterValues.createTime[0]).format('YYYY-MM-DD HH:mm:ss')
          : '',
        createTimeEnd: filterValues.createTime?.length
          ? dayjs(filterValues.createTime[1]).format('YYYY-MM-DD HH:mm:ss')
          : '',
        shopId: currentShopId,
      };
      const res = await api(record.id || record.adId, params);
      if (res?.code === 200 || res?.code === 0) {
        message.success('操作成功');
        fetchList();
      } else {
        message.error(res?.msg || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleToggleStatus = async (record: any, checked: boolean) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const key = record.id || record.adId;
    setStatusUpdatingId(key);
    try {
      const detailRes = await getAdBannerDetailApi(record.id || record.adId);
      if (!(detailRes?.code === 200 || detailRes?.code === 0) || !detailRes?.data) {
        message.error(detailRes?.msg || '获取详情失败');
        return;
      }
      const detail = detailRes.data;
      const status = checked ? 1 : 0;
      const payload: any = {
        ...detail,
        id: detail.id || record.id,
        adId: detail.adId || record.adId,
        status,
        shopId: currentShopId,
      };
      const res = await editAdBannerApi(payload);
      if (res?.code === 200 || res?.code === 0) {
        message.success('状态已更新');
        setData(prev =>
          prev.map(item =>
            (item.id || item.adId) === key
              ? {
                  ...item,
                  status,
                }
              : item,
          ),
        );
      } else {
        message.error(res?.msg || '更新失败');
      }
    } catch (error) {
      message.error('更新失败');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const columns = useMemo(
    () => [
      { title: '广告ID', dataIndex: 'adId' },
      { title: '广告标题', dataIndex: 'title' },
      {
        title: '广告图',
        dataIndex: 'imageUrl',
        render: (v: string) =>
          v ? (
            <Image
              src={v}
              width={80}
              preview={{ mask: null }}
            />
          ) : (
            <Tag>—</Tag>
          ),
      },
      { title: '广告位', dataIndex: 'position' },
      { title: '广告链接', dataIndex: 'linkUrl', ellipsis: true },
      {
        title: '显示状态',
        dataIndex: 'status',
        render: (_: number, record: any) => {
          const key = record.id || record.adId;
          return (
            <Switch
              checked={record.status === 1}
              loading={statusUpdatingId === key}
              onChange={checked => handleToggleStatus(record, checked)}
            />
          );
        },
      },
      { title: '创建时间', dataIndex: 'createTime' },
      {
        title: '操作',
        width: 240,
        render: (_: any, record: any, index: number) => (
          <Space size={15}>
            {checkPermissions(INTEGRAL_HEAD_KEY.EDIT) && (
              <Button
                style={{ padding: 0 }}
                type="link"
                size="small"
                onClick={() => handleEdit(record)}>
                编辑
              </Button>
            )}
            {checkPermissions(INTEGRAL_HEAD_KEY.DELETE) && (
              <Button
                style={{ padding: 0 }}
                type="link"
                size="small"
                onClick={() => handleDelete(record)}>
                删除
              </Button>
            )}
            {checkPermissions(INTEGRAL_HEAD_KEY.UP) && (
              <Button
                style={{ padding: 0 }}
                type="link"
                size="small"
                disabled={index === 0}
                onClick={() => handleMove(record, 'UP')}>
                上移
              </Button>
            )}
            {checkPermissions(INTEGRAL_HEAD_KEY.DOWN) && (
              <Button
                style={{ padding: 0 }}
                type="link"
                size="small"
                disabled={index === data.length - 1}
                onClick={() => handleMove(record, 'DOWN')}>
                下移
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [data.length],
  );

  const handleReset = () => {
    form.resetFields();
    fetchList({ pageNum: 1 });
  };

  const openCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({ status: true });
    setEditing(null);
    setCreateVisible(true);
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      if (!currentShopId) {
        message.warning('请先选择店铺');
        return;
      }
      const img = values.image || {};
      const imageUrl =
        typeof img === 'string'
          ? img
          : img.imageUrl || img.tmUrl || img.jdUrl || img.tmImageUrl || img.jdImageUrl || '';
      const payload = {
        title: values.title,
        position: values.position,
        linkUrl: values.linkUrl,
        status: values.status ? 1 : 0,
        shopId: currentShopId,
        imageUrl,
      };
      const res = editing
        ? await editAdBannerApi({ ...payload, id: editing.id, adId: editing.adId, sortWeight: editing.sortWeight })
        : await addAdBannerApi(payload);
      if (res?.code === 200 || res?.code === 0) {
        message.success(editing ? '保存成功' : '新建成功');
        setCreateVisible(false);
        fetchList({ pageNum: 1 });
      } else {
        message.error(res?.msg || (editing ? '保存失败' : '新建失败'));
      }
    } catch (error) {
      if ((error as any)?.errorFields) return;
      message.error(editing ? '保存失败' : '新建失败');
    }
  };

  return (
    <Space
      direction="vertical"
      size={16}
      style={{ width: '100%', padding: 24 }}>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12, columnGap: 12 }}>
        <Form.Item
          label="广告位"
          name="position">
          <Select
            options={[{ label: '全部', value: '' }, ...positions]}
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="广告标题"
          name="title">
          <Input
            placeholder="请输入"
            allowClear
            style={{ width: 200 }}
            maxLength={20}
          />
        </Form.Item>
        <Form.Item
          label="显示状态"
          name="status">
          <Select
            options={[
              { label: '全部', value: undefined },
              { label: '显示', value: 1 },
              { label: '隐藏', value: 0 },
            ]}
            style={{ width: 140 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="创建时间"
          name="createTime">
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
          />
        </Form.Item>
        <Form.Item>
          <Space size={12}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Space style={{ marginBottom: 12 }}>
        {checkPermissions(INTEGRAL_HEAD_KEY.ADD) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}>
            新建
          </Button>
        )}
      </Space>

      <Table
        rowKey={record => record.id || `${record.adId}-${record.title}`}
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (pn, ps) => fetchList({ pageNum: pn, pageSize: ps }),
        }}
      />

      <Modal
        title="新建广告"
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={handleCreate}
        destroyOnHidden
        width={600}>
        <Form
          layout="horizontal"
          labelCol={{ style: { width: 100 } }}
          form={createForm}>
          <Form.Item
            label="广告标题"
            name="title"
            rules={[{ required: true, message: '请输入广告标题' }]}>
            <Input
              placeholder="请输入"
              maxLength={20}
              showCount
            />
          </Form.Item>
          <Form.Item
            label="资源位"
            name="position"
            rules={[{ required: true, message: '请选择资源位' }]}>
            <Select
              placeholder="请选择"
              options={positions}
            />
          </Form.Item>
          <Form.Item
            label="广告图"
            name="image"
            rules={[{ required: true, message: '请上传广告图' }]}>
            <ImageUploader
              limitWidth={750}
              limitHeight={410}
              maxSizeKB={1024 * 5}
            />
          </Form.Item>
          <Form.Item
            label="跳转链接"
            name="linkUrl"
            rules={[{ required: true, message: '请输入跳转链接' }]}>
            <Input placeholder="请输入" />
          </Form.Item>
          <Form.Item
            label="立刻显示"
            name="status"
            valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default IntegralMallHead;
